/**
 * Bazaarvoice PDP global overrides (BV third-party DOM).
 * Rating summary (inline row + histogram popup) + full reviews section (`#product-bazaarvoice-reviews`).
 *
 * Figma refs:
 * - Customer Reviews section mobile: 10339:44092 (padding 48px 20px, gap 44px)
 * - Inline row desktop: 11183:76926 | mobile: 11183:74762
 * - Sort dropdown button desktop: 11183:77799
 * - Histogram row desktop: 11183:77717 | mobile: 11183:75632
 * - Mobile filter/sort toolbar: 11183:75667
 * - Review list item profile + body: 10892:125369
 */

import typography from '@amblique/king-living/app/theme/foundations/typography';

const bvShow = '[data-bv-show="rating_summary"]';
const bvRoot = `& .sf-bazaarvoice-inline-rating ${bvShow}`;
const bvMainContainer = `${bvRoot} .bv_main_container`;
const bvButtonsRow = `${bvRoot} .bv_rating_summary_buttons_container`;
const bvRatingButton = `${bvRoot} .bv_main_rating_button`;
const bvStarsWrap = `${bvRoot} .bv_stars_component_container`;
const bvScore = `${bvRoot} .bv_avgRating_component_container`;
const bvReviews = `${bvRoot} .bv_numReviews_text`;
const bvTileRoot = `& .sf-bazaarvoice-inline-rating[data-bv-variant="tile"] ${bvShow}`;
const bvTileScore = `${bvTileRoot} .bv_main_container .bv_avgRating_component_container`;
const bvTileReviews = `${bvTileRoot} .bv_main_container .bv_numReviews_text`;
const bvStripDesktop = `& [data-testid="add-to-cart-strip-desktop"] .sf-bazaarvoice-inline-rating[data-bv-variant="strip"] ${bvShow}`;
const bvHistogramPopup = [
  `${bvRoot} .histogram_hover`,
  '& [data-bv-show="rating_summary"] .histogram_hover',
  `${bvRoot} .bv_histogram`,
  '& .bv_modal_component_container[data-bv-modal] .bv_modal_outer_content',
  '& .bv_modal_outer_content',
].join(', ');
const bvModalOuter = [
  '& .bv_modal_component_container[data-bv-modal] .bv_modal_outer_content',
  '& .bv_modal_outer_content',
].join(', ');
const bvModalShell = [
  '& .bv_modal_component_container[data-bv-modal]',
  '& .bv_modal_component_container[data-bv-modal] .bv_modal_inner_content',
  '& .bv_modal_outer_content',
  '& .histogram_hover',
].join(', ');
const bvModalInner = [
  '& .bv_modal_component_container[data-bv-modal] .bv_modal_inner_content',
  '& .bv_modal_inner_content',
].join(', ');
const bvHistogramButtonWrap = [
  '& .bv_modal_outer_content .bv_button_component_container',
  '& .bv_button_component_container',
].join(', ');
const bvHistogramList = [
  `${bvRoot} .bv_histogram_list_container`,
  '& .histogram_hover .bv_histogram_list_container',
  '& .bv_modal_outer_content .bv_histogram_list_container',
].join(', ');
const bvHistogramRow = [
  `${bvRoot} .bv_histogram_row_container`,
  '& [data-bv-show="rating_summary"] .bv_histogram_row_container',
  '& .histogram_hover .bv_histogram_row_container',
  '& .bv_modal_outer_content .bv_histogram_row_container',
  '& .bv_modal_component_container[data-bv-modal] .bv_histogram_row_container',
].join(', ');
const bvHistogramPrefix = [
  `${bvRoot} .bv_histogram_row_prefix`,
  '& .histogram_hover .bv_histogram_row_prefix',
  '& .bv_modal_outer_content .bv_histogram_row_prefix',
  '& .bv_modal_component_container[data-bv-modal] .bv_histogram_row_prefix',
].join(', ');
const bvHistogramStar = [
  `${bvRoot} .bv_histogram_row_star`,
  '& .histogram_hover .bv_histogram_row_star',
  '& .bv_modal_outer_content .bv_histogram_row_star',
].join(', ');
const bvHistogramBar = [
  `${bvRoot} .bv_histogram_row_full_bar`,
  '& .histogram_hover .bv_histogram_row_full_bar',
  '& .bv_modal_outer_content .bv_histogram_row_full_bar',
].join(', ');
const bvHistogramBarFill = [
  `${bvRoot} .bv_histogram_row_bar`,
  '& .histogram_hover .bv_histogram_row_bar',
  '& .bv_modal_outer_content .bv_histogram_row_bar',
].join(', ');
const bvHistogramCount = [
  `${bvRoot} .bv_histogram_row_rating`,
  '& .histogram_hover .bv_histogram_row_rating',
  '& .bv_modal_outer_content .bv_histogram_row_rating',
].join(', ');
const bvHistogramReadReviews = [
  `${bvRoot} .bv_button_buttonFull`,
  `${bvRoot} button.bv_button_buttonFull`,
  `${bvRoot} .bv_button_button_full`,
  `${bvRoot} a.bv_button_button_full`,
  `${bvRoot} button.bv_button_button_full`,
  '& .histogram_hover .bv_button_buttonFull',
  '& .histogram_hover button.bv_button_buttonFull',
  '& .histogram_hover .bv_button_button_full',
  '& .histogram_hover a.bv_button_button_full',
  '& .histogram_hover button.bv_button_button_full',
  '& .bv_modal_outer_content .bv_button_buttonFull',
  '& .bv_modal_outer_content button.bv_button_buttonFull',
  '& .bv_modal_outer_content .bv_button_button_full',
  '& .bv_modal_outer_content a.bv_button_button_full',
  '& .bv_modal_outer_content button.bv_button_button_full',
  '& .bv_button_component_container button',
].join(', ');
const bvReviewsSection = '& #product-bazaarvoice-reviews';
/**
 * Append a suffix to every branch of a selector list.
 * `${'A, B'} .x` → `A, B .x` (wrong); use this → `A .x, B .x`.
 */
const bvSelAll = (selectors, suffix = '') =>
  selectors.map((s) => `${s}${suffix}`).join(', ');
/** Force King Living typography — BV ships serif body/heading stacks in styled-components. */
const bvFontFamilyReset = {
  fontFamily: 'body !important',
};
const bvReviewsFontReset = `${bvReviewsSection}, ${bvReviewsSection} *`;
const bvInlineRatingFontReset = `${bvRoot}, ${bvRoot} *`;
const bvReviewsPopupFontReset = '& div[id^="bv-popup-"], & div[id^="bv-popup-"] *';
const bvReviewsTitle = `${bvReviewsSection} #bv_review_maincontainer h2`;
/** Title wrap = direct child of main that contains the Reviews h2. */
const bvReviewsTitleWrap = `${bvReviewsSection} #bv_review_maincontainer > div:has(> h2)`;
/**
 * Client-response nest = 2nd child under content body wrap when a reply exists
 * (`… > div:nth-child(2) > div`). Require nested box so empty placeholders don't match.
 */
const bvReviewsResponseOuter =
  `${bvReviewsSection} [data-bv-v="contentItem"] > div:not([data-bv-v]) > div:nth-child(2):has(> div)`;
const bvReviewsContentHeader = `${bvReviewsSection} [data-bv-v="contentHeader"]`;
/** Rating Snapshot / Overall / empty column row — stable BV layout classes. */
const bvReviewsRatingSummaryRow =
  `${bvReviewsSection} #bv_review_maincontainer div:has(> .bv_rating_content1)`;
const bvReviewsRatingSummaryRowInner =
  `${bvReviewsSection} #bv_review_maincontainer > div:has(.bv_rating_content1)`;
/** Desktop-only spacer between rating summary and filter (absent on narrow viewports). */
const bvReviewsRatingFilterSpacer =
  `${bvReviewsSection} #bv_review_maincontainer > div:has(+ #bv_review_filter_container):not(:has(.bv_rating_content1))`;
const bvReviewsFilterContainer = `${bvReviewsSection} #bv_review_filter_container`;
/** Relative chip node — use for adjacent-sibling rules (avoid repeating full root). */
const bvReviewsFilterChipRel =
  `div:has(> [role="combobox"][aria-label*="Filter by"])`;
/** Rating / Locale filter chips inside the filter container. */
const bvReviewsFilterChip = `${bvReviewsFilterContainer} ${bvReviewsFilterChipRel}`;
/** Snapshot / Overall / Filter headings — stable ids + filter h3. */
const bvReviewsSnapshotHeading = [
  `${bvReviewsSection} #bv-reviews-rating-snapshot-container`,
  `${bvReviewsSection} #bv-reviews-overall-ratings-container h3`,
  `${bvReviewsSection} #bv_review_filter_container > h3`,
].join(', ');
/** Histogram / overall score body wraps — ids + stable `.bv_rating_content*` parents. */
const bvReviewsSnapshotBody = [
  `${bvReviewsSection} .bv_rating_content1 > section`,
  `${bvReviewsSection} #bv-reviews-rating-snapshot-container ~ div`,
  `${bvReviewsSection} #bv-reviews-overall-ratings-container > div`,
].join(', ');
/** Count row sits immediately before `#reviews_container`. */
const bvReviewsCountRow =
  `${bvReviewsSection} #bv_review_maincontainer > div:has(+ #reviews_container)`;
/** Inner flex bar: review count + Sort + Relevancy (BV applies 10px side margins). */
const bvReviewsCountBar =
  `${bvReviewsCountRow} > div:has([role="combobox"][aria-label*="Sort by"])`;
/** Pagination row sits immediately after `#reviews_container`. */
const bvReviewsPaginationRow = `${bvReviewsSection} #reviews_container + div`;
/** Inner flex bar: review count + prev/next (BV applies 10px side margins). */
const bvReviewsPaginationBar =
  `${bvReviewsPaginationRow} > div:has(.prev, .next)`;
const bvReviewsPaginationControls =
  `${bvReviewsPaginationRow} ul[role="presentation"]`;
/** Individual review rows + list item layout (profile + body). */
const bvReviewsReviewRow = `${bvReviewsSection} section[id^="bv-review-"]`;
const bvReviewsContentItem = `${bvReviewsSection} [data-bv-v="contentItem"]`;
const bvReviewsContentBody =
  `${bvReviewsSection} [data-bv-v="contentItem"] > div:not([data-bv-v])`;
/** Alias of `bvReviewsContentHeader` for stars-row composition only (not a 2nd style key). */
const bvReviewsItemStarsBlock = bvReviewsContentHeader;
/** Stars row = first child with role=img inside contentHeader. */
const bvReviewsItemStarsRow = `${bvReviewsItemStarsBlock} > [role="img"]`;
const bvReviewsItemContentPad = `${bvReviewsSection} [data-bv-v="contentSummary"]`;
/**
 * Mobile filter/sort toolbar (iPhone emulation verified):
 * `#bv_review_maincontainer > div` that hosts `role=button[aria-haspopup=dialog]`
 * (Filters / Sort). Prefer ARIA + hierarchy over `sc-107gs2s` / `sc-1kd7i6j` / `wnep33-*`.
 */
const bvReviewsMobileToolbarOuter = `${bvReviewsSection} #bv_review_maincontainer > div:has([role="button"][aria-haspopup="dialog"]):not(#bv_review_filter_container)`;
/** Direct review card wrapper under `section[id^=bv-review-]`. */
const bvReviewsItemWrapper = `${bvReviewsReviewRow} > div`;
const bvReviewsInlineProfile = `${bvReviewsSection} [data-bv-v="inlineProfile"]`;
/**
 * Profile column nest (was `emkap-*`) — no data-bv-v; hierarchy under inlineProfile:
 * `> div` root → username col / stats col → avatar / reviews / location slots.
 */
const bvReviewsProfileRoot = `${bvReviewsInlineProfile} > div`;
const bvReviewsProfileEmkap = [
  bvReviewsProfileRoot,
  `${bvReviewsProfileRoot} > div:first-child`,
  `${bvReviewsProfileRoot} > div:last-child`,
  `${bvReviewsProfileRoot} > div:last-child > div:nth-child(1)`,
  `${bvReviewsProfileRoot} > div:last-child > div:nth-child(2)`,
  `${bvReviewsProfileRoot} > div:last-child > div:nth-child(3)`,
].join(', ');
const bvReviewsProfileStatsNest =
  `${bvReviewsProfileRoot} > div:last-child > div:nth-child(2) div`;
const bvReviewsProfileEmptyAvatar =
  `${bvReviewsProfileRoot} > div:last-child > div:first-child:not(:has(img, svg))`;
const bvReviewsProfileEmptyLocation =
  `${bvReviewsProfileRoot} > div:last-child > div:last-child:not(:has(:not(:empty)))`;
/** Review profile column — Figma 10892:125369 */
const bvReviewsProfileUsernameButton =
  `${bvReviewsInlineProfile} button.bv-rnr-action-bar`;
const bvReviewsProfileUsername = `${bvReviewsProfileUsernameButton} > span`;
const bvReviewsProfileStatsLabel =
  `${bvReviewsInlineProfile} > div > div:last-child > div:nth-child(2) span:first-of-type`;
const bvReviewsProfileStatsCount =
  `${bvReviewsInlineProfile} > div > div:last-child > div:nth-child(2) span:last-of-type`;
/** Review item content — Figma 10892:125369 */
const bvReviewsItemTitleDateRow =
  `${bvReviewsContentHeader} > div:not([role="img"])`;
const bvReviewsItemTitle = `${bvReviewsContentHeader} h3`;
const bvReviewsItemDateWrap = `${bvReviewsItemTitleDateRow} > div`;
const bvReviewsItemMetaInner = `${bvReviewsItemDateWrap} > div`;
/** Duplicate author chip in title/date row — keep profile-column username only. */
const bvReviewsItemMetaAuthorSlot = `${bvReviewsItemTitleDateRow} button.bv-rnr-action-bar`;
/**
 * Duplicate Verified badge slot beside title-row date.
 * Meta inner: [0] author, [1] verified (optional), [2] date.
 * `:not(:last-child)` avoids hiding the date when BV omits the badge slot.
 */
const bvReviewsItemMetaBadgeSlot =
  `${bvReviewsItemMetaInner} > div:nth-child(2):not(:last-child)`;
const bvReviewsItemMetaDateSlot = `${bvReviewsItemMetaInner} > div:last-child`;
const bvReviewsItemDate = `${bvReviewsItemMetaDateSlot} span`;
const bvReviewsItemBody =
  `${bvReviewsSection} [data-bv-v="contentSummary"] [id^="bv-review-text-"]`;
/** Client response card — structural under content body last child. */
const bvReviewsResponseBox = `${bvReviewsResponseOuter} > div`;
const bvReviewsResponseHeader = `${bvReviewsResponseBox} > div:first-child`;
const bvReviewsResponseHeaderIcon = `${bvReviewsResponseHeader} > div:first-child`;
const bvReviewsResponseHeaderMeta = `${bvReviewsResponseHeader} > div:last-child`;
const bvReviewsResponseLabelCell = `${bvReviewsResponseHeaderMeta} > div:first-child`;
const bvReviewsResponseDateCell = `${bvReviewsResponseHeaderMeta} > div:last-child`;
const bvReviewsResponseLabel = `${bvReviewsResponseLabelCell} > div`;
const bvReviewsResponseDate = `${bvReviewsResponseDateCell} span`;
const bvReviewsResponseContent = `${bvReviewsResponseBox} > div:last-child`;
const bvReviewsResponseContentInner = `${bvReviewsResponseContent} > div`;
const bvReviewsResponseContentTitle = `${bvReviewsResponseContentInner} > div:first-child`;
const bvReviewsResponseContentBody = `${bvReviewsResponseContentInner} > div:last-child`;
/** Mobile filter + sort toolbar row / cells / dialog buttons — Figma 11183:75667 */
const bvReviewsMobileToolbarRow =
  `${bvReviewsMobileToolbarOuter} > div:has(> div [role="button"][aria-haspopup="dialog"])`;
const bvReviewsMobileToolbarCell = `${bvReviewsMobileToolbarRow} > div`;
const bvReviewsMobileToolbarButton =
  `${bvReviewsMobileToolbarRow} [role="button"][aria-haspopup="dialog"]`;
const bvReviewsMobileToolbarButtonIcon = `${bvReviewsMobileToolbarButton} > span`;
const bvReviewsMobileToolbarButtonBody = `${bvReviewsMobileToolbarButton} > div`;
/** Sort control = dialog button whose body has label + value lines. */
const bvReviewsMobileToolbarSortButton =
  `${bvReviewsMobileToolbarRow} [role="button"][aria-haspopup="dialog"]:has(> div > div + div)`;
const bvReviewsMobileToolbarSortLabel =
  `${bvReviewsMobileToolbarSortButton} > div > div:first-child`;
const bvReviewsMobileToolbarSortValue =
  `${bvReviewsMobileToolbarSortButton} > div > div:last-child`;
const bvReviewsMobileToolbarSortStack =
  `${bvReviewsMobileToolbarCell}:last-child > div`;
/** Sort controls — anchored to stable dropdown ids / aria. */
const bvReviewsSortCombobox =
  `${bvReviewsSection} [role="combobox"][aria-label*="Sort by"]`;
const bvReviewsSortLabel = `${bvReviewsSection} #bv-dropdown-select-reviews`;
/** Bordered Sort button = direct parent of the combobox. */
const bvReviewsSortControl =
  `${bvReviewsSection} div:has(> [role="combobox"][aria-label*="Sort by"])`;
/** "Sort by" label wrap inside the button (not the button itself). */
const bvReviewsSortShell =
  `${bvReviewsSection} div:has(> #bv-dropdown-select-reviews)`;
const bvReviewsSortStack =
  `${bvReviewsCountRow} > div:last-child > div:has([role="combobox"][aria-label*="Sort by"])`;
const bvReviewsSortInner = `${bvReviewsSortStack} > div`;
/**
 * BV outer chrome around the Sort button (`outer > mid > control`).
 * Avoid nested `:has()` (invalid in some engines). Scope under sort inner row.
 * Do NOT use `div:has(> combobox)` — that is the control and needs padding.
 */
const bvReviewsSortDropdown =
  `${bvReviewsSortInner} > div:has([role="combobox"][aria-label*="Sort by"])`;
const bvReviewsSortDropdownMid = `${bvReviewsSortDropdown} > div`;
/**
 * Relevancy info: desktop sits in the count row; mobile sits in the filter/sort
 * toolbar outer (above Filters / Sort). Keep branches in an array + `bvSelAll`.
 */
const bvReviewsRelevancyWrapSelectors = [
  `${bvReviewsCountRow} div:has(> span > button)`,
  `${bvReviewsMobileToolbarOuter} div:has(> span > button)`,
];
const bvReviewsRelevancyWrap = bvReviewsRelevancyWrapSelectors.join(', ');
const bvRelevancyInfo = bvSelAll(bvReviewsRelevancyWrapSelectors, ' button');
/** Visible icon + "Relevancy Info" label inside the button. */
const bvRelevancyInfoLabel = bvSelAll(
  bvReviewsRelevancyWrapSelectors,
  ' button > span[aria-hidden="true"]'
);
/** SR-only copy inside Relevancy button (sibling after visible `aria-hidden` label). */
const bvRelevancySrOnly = bvSelAll(
  bvReviewsRelevancyWrapSelectors,
  ' button > span[aria-hidden="true"] + span'
);
/** BV Relevancy info popover — id suffix is dynamic (`bv-popup-47`, etc.). */
const bvRelevancyPopupSelectors = [
  `${bvReviewsSection} div[id^="bv-popup-"]`,
  '& div[id^="bv-popup-"]',
];
const bvRelevancyPopup = bvRelevancyPopupSelectors.join(', ');
const bvRelevancyPopupClose = bvSelAll(bvRelevancyPopupSelectors, ' .PopoverClose');
const bvReviewsStarsAbbr = `${bvReviewsSection} abbr[aria-hidden="true"]`;
/**
 * Verified badge button — keep branches as an array and expand with `bvSelAll`
 * so derived `.badge-label` selectors apply to every branch (not only the last).
 */
const bvVerifiedBadgeSelectors = [
  `${bvReviewsInlineProfile} button[aria-label="Verified Purchaser"]`,
  `${bvReviewsInlineProfile} button:has(> .badge-label)`,
];
const bvVerifiedBadge = bvVerifiedBadgeSelectors.join(', ');
const bvVerifiedBadgeList = `${bvReviewsInlineProfile} ul[role="presentation"]`;
/**
 * Profile columns: [0] username, [1] verified badge slot, [2] stats ("Reviews N").
 * Middle slot only — `:not(:last-child)` avoids the stats column when badge is omitted.
 */
const bvVerifiedBadgeSlot =
  `${bvReviewsInlineProfile} > div > div:nth-child(2):not(:last-child)`;
const bvVerifiedBadgeWrap = bvSelAll(bvVerifiedBadgeSelectors, ' .badge-label');
const bvVerifiedBadgeText = bvSelAll(
  bvVerifiedBadgeSelectors,
  ' .badge-label > span:last-child'
);
const bvVerifiedBadgeIcon = bvSelAll(
  bvVerifiedBadgeSelectors,
  ' .badge-label > span:first-child'
);
const bvReviewsPaginationPrev = `${bvReviewsSection} .prev`;
const bvReviewsPaginationNext = `${bvReviewsSection} .next`;
const bvReviewsPaginationPrevDisabled =
  `${bvReviewsPaginationPrev}:disabled, ${bvReviewsPaginationPrev}[aria-disabled="true"], ${bvReviewsPaginationPrev}.disabled`;
const bvReviewsPaginationNextDisabled =
  `${bvReviewsPaginationNext}:disabled, ${bvReviewsPaginationNext}[aria-disabled="true"], ${bvReviewsPaginationNext}.disabled`;

/** Figma KL-Designs 11183:77933 (disabled) / 11183:77934 (active) — box only; keep BV arrow SVG. */
const bvReviewsPaginationButtonBase = {
  alignItems: 'center',
  backgroundColor: 'white',
  border: '1px solid #E8E8E8',
  borderRadius: '4px',
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  height: '52px',
  justifyContent: 'center',
  lineHeight: '1',
  minHeight: '52px',
  minWidth: '52px',
  padding: '16px',
  textDecoration: 'none',
  width: '52px',
  '& > span, & > div': {
    alignItems: 'center',
    display: 'flex',
    height: '20px',
    justifyContent: 'center',
    margin: '0',
    padding: '0',
    width: '20px',
  },
  '& svg': {
    display: 'block',
    flexShrink: 0,
    height: '20px',
    maxHeight: '20px',
    maxWidth: '20px',
    width: '20px',
  },
};

/** 13px stars — BV renders 20px SVGs inline in `.bv_stars_component_container`. */
const bvStarClip = {
  [bvStarsWrap]: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    flexWrap: 'nowrap',
    gap: '4px',
    height: '13px !important',
    lineHeight: 0,
    margin: '0 !important',
    padding: '0 !important',
    '& svg': {
      display: 'block',
      flexShrink: 0,
      height: '13px !important',
      margin: '0 !important',
      padding: '0 !important',
      paddingRight: '4px !important',
      width: '13px !important',

      '&:last-child': {
        paddingRight: '0 !important',
      },
    },
  },
};

export const bazaarvoicePdpGlobalStyles = {
  ...bvStarClip,
  [bvReviewsFontReset]: bvFontFamilyReset,
  [bvInlineRatingFontReset]: bvFontFamilyReset,
  [bvReviewsPopupFontReset]: bvFontFamilyReset,
  [bvMainContainer]: {
    alignItems: 'center',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
    border: 'none !important',
    borderStyle: 'none !important',
    display: 'inline-flex !important',
    flexWrap: 'nowrap',
    gap: '8px',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto',
  },
  [bvButtonsRow]: {
    alignItems: 'center',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
    display: 'inline-flex',
    flexWrap: 'nowrap',
    gap: '8px !important',
  },
  [bvRatingButton]: {
    alignItems: 'center',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
    border: 'none !important',
    borderStyle: 'none !important',
    color: 'inherit !important',
    cursor: 'pointer',
    display: 'inline-flex',
    flexShrink: 0,
    flexWrap: 'nowrap',
    gap: '8px !important',
    margin: '0 !important',
    padding: '0 !important',
    textDecoration: 'none !important',
    '&:hover, &:focus, &:active': {
      background: 'transparent !important',
      backgroundColor: 'transparent !important',
      border: 'none !important',
      boxShadow: 'none !important',
    },
  },
  [bvScore]: {
    ...typography.textStyles.bodyS,
    alignItems: 'baseline',
    color: '#282829 !important',
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    margin: '0 !important',
    padding: '0 !important',
    whiteSpace: 'nowrap',
    '&::after': {
      content: '"Stars"',
      marginLeft: '3px',
    },
  },
  [`${bvRoot} .bv_numReviews_component_container`]: {
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
    display: 'inline-flex',
    flexShrink: 0,
    margin: '0 !important',
    padding: '0 !important',
  },
  [`${bvRoot} .bv_offscreen_text`]: {
    border: '0',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  },
  [bvReviews]: {
    ...typography.textStyles.labelS,
    color: '#8C683B !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    textDecoration: 'underline !important',
    textDecorationLine: 'underline !important',
    textDecorationSkipInk: 'none !important',
    textDecorationStyle: 'solid !important',
    textUnderlineOffset: '35% !important',
    textUnderlinePosition: 'from-font !important',
    whiteSpace: 'nowrap',
  },
  [bvTileScore]: {
    display: 'none !important',
  },
  [bvTileReviews]: {
    ...typography.textStyles.bodyS,
    color: '#282829 !important',
    fontSize: '12px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    textDecoration: 'none !important',
    textDecorationLine: 'none !important',
    whiteSpace: 'nowrap',
  },
  [`${bvStripDesktop} .bv_avgRating_component_container`]: {
    color: 'rgba(27, 26, 24, 0.7) !important',
    fontSize: '14px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.4 !important',
    '&::after': {
      content: '"Stars"',
      marginLeft: '3px',
    },
  },
  [`${bvStripDesktop} .bv_numReviews_text`]: {
    fontSize: '13px !important',
    lineHeight: '1.2 !important',
  },
  [bvModalShell]: {
    borderRadius: '0 !important',
    overflow: 'visible !important',
  },
  [bvModalInner]: {
    margin: '0 !important',
    padding: '0 !important',
  },
  [bvModalOuter]: {
    backgroundColor: 'white !important',
    border: '1px solid #282829 !important',
    borderRadius: '0 !important',
    boxShadow: '0 4px 16px rgba(27, 26, 24, 0.12) !important',
    boxSizing: 'border-box !important',
    color: '#282829 !important',
    display: 'block !important',
    flexWrap: 'nowrap !important',
    fontFamily: 'body',
    margin: '0 !important',
    maxWidth: {base: '280px !important', lg: '317px !important'},
    padding: {base: '12px 16px !important', lg: '12px 16px !important'},
    width: 'auto !important',
    zIndex: 'popover',
    '& .bv_histogram, & ul': {
      margin: '0 !important',
      padding: '0 !important',
      width: '100% !important',
    },
  },
  [bvHistogramPopup]: {
    backgroundColor: 'white',
    border: '1px solid #282829',
    borderRadius: '0 !important',
    boxShadow: '0 4px 16px rgba(27, 26, 24, 0.12)',
    boxSizing: 'border-box',
    color: '#282829',
    fontFamily: 'body',
    maxWidth: {base: '280px', lg: '317px'},
    padding: {base: '12px 16px', lg: '12px 16px'},
    zIndex: 'popover',
  },
  [bvHistogramList]: {
    display: 'block !important',
    listStyle: 'none !important',
    margin: '0 !important',
    padding: '0 !important',
    width: '100% !important',
    '& + .bv_histogram_list_container': {
      marginTop: '8px !important',
    },
  },
  [bvHistogramRow]: {
    alignItems: 'center !important',
    color: '#000000 !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    flexWrap: 'nowrap !important',
    fontFamily: 'body',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontWeight: '600 !important',
    gap: '8px !important',
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    margin: '0 !important',
    minHeight: '20px !important',
    padding: '0 !important',
    textDecoration: 'none !important',
    whiteSpace: 'nowrap !important',
    width: '100% !important',
  },
  [bvHistogramPrefix]: {
    alignItems: 'center !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    gap: '4px !important',
    margin: '0 !important',
    minWidth: '28px !important',
    padding: '0 !important',
    verticalAlign: 'middle !important',
    whiteSpace: 'nowrap !important',
    width: 'auto !important',
    '& > *': {
      display: 'inline-flex !important',
      flexShrink: '0 !important',
    },
  },
  [bvHistogramStar]: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    lineHeight: 0,
    '& svg': {
      display: 'block',
      flexShrink: 0,
      height: '16px !important',
      width: '16px !important',
    },
  },
  [bvHistogramBar]: {
    backgroundColor: '#F0F0F0 !important',
    borderRadius: '100px !important',
    boxShadow: 'none !important',
    flex: '1 1 auto !important',
    height: '8px !important',
    margin: '0 !important',
    maxWidth: '100% !important',
    minWidth: '0 !important',
    overflow: 'hidden !important',
    padding: '0 !important',
    position: 'relative !important',
    width: 'auto !important',
  },
  [bvHistogramBarFill]: {
    backgroundColor: 'rgba(27, 26, 24, 0.9) !important',
    borderRadius: '100px !important',
    height: '100% !important',
    minWidth: '0 !important',
  },
  [bvHistogramCount]: {
    color: '#000000 !important',
    flexShrink: '0 !important',
    fontFamily: 'body',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontWeight: {base: '400 !important', lg: '600 !important'},
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    minWidth: '24px',
    textAlign: 'right !important',
    whiteSpace: 'nowrap !important',
  },
  [bvHistogramButtonWrap]: {
    marginTop: '12px !important',
    padding: '0 !important',
    textAlign: 'center !important',
    width: '100% !important',
  },
  [bvHistogramReadReviews]: {
    alignItems: 'center',
    backgroundColor: '#1B1A18 !important',
    backgroundImage: 'none !important',
    border: '1px solid #1B1A18 !important',
    borderRadius: '0 !important',
    boxShadow: 'none !important',
    boxSizing: 'border-box !important',
    color: 'white !important',
    cursor: 'pointer',
    display: 'inline-flex !important',
    flexShrink: '0 !important',
    fontFamily: 'body',
    fontSize: {base: '14px !important', lg: '14px !important'},
    fontWeight: '600 !important',
    height: '44px !important',
    justifyContent: 'center',
    letterSpacing: '0 !important',
    lineHeight: '1.4 !important',
    margin: '0 auto !important',
    maxHeight: '44px !important',
    minHeight: '44px !important',
    minWidth: '165px !important',
    padding: '0 16px !important',
    textAlign: 'center',
    textDecoration: 'none !important',
    transform: 'none !important',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease !important',
    width: 'auto !important',
    '&:hover, &:focus, &:active': {
      backgroundColor: '#282829 !important',
      backgroundImage: 'none !important',
      border: '1px solid #282829 !important',
      boxShadow: 'none !important',
      color: 'white !important',
      fontSize: {base: '14px !important', lg: '14px !important'},
      height: '44px !important',
      maxHeight: '44px !important',
      minHeight: '44px !important',
      minWidth: '165px !important',
      padding: '0 16px !important',
      textDecoration: 'none !important',
      transform: 'none !important',
      width: 'auto !important',
    },
  },
  // Hide incomplete PDP inline ratings only — never target PLP `tile` variant.
  '& [data-testid="add-to-cart-strip-mobile"] .sf-bazaarvoice-inline-rating, .sf-bazaarvoice-inline-rating:not([data-bv-variant="tile"]):not(:has(.bv_rating_summary_buttons_container)), .sf-bazaarvoice-inline-rating:not([data-bv-variant="tile"]):has(.bv_hide_visibility)': {
    display: 'none',
  },
  [`${bvReviewsSection}:not(:has([data-bv-v="contentHeader"])), ${bvReviewsSection}:has(.bv_hide_visibility)`]: {
    display: 'none',
    margin: 0,
    padding: 0,
  },
  [bvReviewsSection]: {
    backgroundColor: 'white',
    // Figma 10339:44092 — mobile section padding 48px 20px; desktop keeps 100 / 80.
    paddingBottom: {base: '48px', lg: '100px'},
    paddingTop: {base: '48px', lg: '100px'},
    marginTop: {base: '-60px', lg: '-64px'},
    '& > div': {
      boxSizing: 'border-box',
      margin: '0 auto',
      maxWidth: '1440px',
      paddingX: {base: '20px', lg: '80px'},
      width: '100%',
    },
  },
  [bvReviewsTitleWrap]: {
    boxSizing: 'border-box !important',
    margin: '0 !important',
    maxWidth: '100% !important',
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsTitle]: {
    ...typography.textStyles.headingXL,
    color: 'brandColors.greyscale90',
    fontFamily: 'heading',
    fontSize: {base: '32px', lg: '40px'},
    fontStyle: 'normal',
    fontWeight: '500',
    letterSpacing: {base: '-0.32px', lg: '-0.4px'},
    lineHeight: '1.2',
    margin: '0 0 44px 0',
  },
  [bvReviewsResponseOuter]: {
    boxSizing: 'border-box !important',
    margin: '0 !important',
    maxWidth: '100% !important',
    padding: '0 !important',
    width: '100% !important',
  },
  /**
   * Single style key for contentHeader.
   * `bvReviewsItemStarsBlock` is only an alias — do not add a second object key
   * (duplicate keys silently overwrite; keep the live column layout here).
   */
  [bvReviewsContentHeader]: {
    alignItems: 'flex-start !important',
    boxSizing: 'border-box !important',
    display: 'flex !important',
    flexDirection: 'column !important',
    gap: {base: '10px !important', lg: '12px !important'},
    margin: '0 !important',
    marginBottom: '15px !important',
    padding: '0 !important',
    textAlign: 'left !important',
    width: '100% !important',
  },
  [bvReviewsRatingSummaryRow]: {
    // Section already has 20px L/R on mobile — strip BV card inset + border.
    border: 'none !important',
    borderBottom: 'none !important',
    boxSizing: 'border-box !important',
    marginLeft: '0 !important',
    marginRight: '0 !important',
    paddingBottom: {base: '0 !important', lg: '28px !important'},
    paddingLeft: {base: '0 !important', lg: 'unset'},
    paddingRight: {base: '0 !important', lg: 'unset'},
    gap: {lg: '80px'},
    width: '100% !important',
  },
  [bvReviewsRatingSummaryRowInner]: {
    border: 'none !important',
    boxSizing: 'border-box !important',
    marginLeft: '0 !important',
    marginRight: '0 !important',
    paddingLeft: {base: '0 !important', lg: 'unset'},
    paddingRight: {base: '0 !important', lg: 'unset'},
    width: '100% !important',
  },
  [bvReviewsSnapshotHeading]: {
    boxSizing: 'border-box !important',
    marginLeft: {base: '0 !important', lg: 'unset'},
    marginRight: {base: '0 !important', lg: 'unset'},
    // BV default `padding: 10px` — kill horizontal inset on mobile (section already 20px).
    paddingLeft: {base: '0 !important', lg: 'unset'},
    paddingRight: {base: '0 !important', lg: 'unset'},
  },
  [bvReviewsSnapshotBody]: {
    boxSizing: 'border-box !important',
    marginLeft: {base: '0 !important', lg: 'unset'},
    marginRight: {base: '0 !important', lg: 'unset'},
    paddingLeft: {base: '0 !important', lg: 'unset'},
    paddingRight: {base: '0 !important', lg: 'unset'},
    width: {base: '100% !important', lg: 'unset'},
  },
  [bvReviewsRatingFilterSpacer]: {
    marginLeft: {base: '0 !important', lg: 'unset'},
    marginRight: {base: '0 !important', lg: 'unset'},
    paddingLeft: {base: '0 !important', lg: 'unset'},
    paddingRight: {base: '0 !important', lg: 'unset'},
  },
  [bvReviewsFilterContainer]: {
    alignItems: {base: 'flex-start !important', lg: 'center !important'},
    alignSelf: {base: 'flex-start !important', lg: 'unset !important'},
    border: 'none !important',
    borderBottom: 'none !important',
    boxSizing: 'border-box !important',
    display: 'flex',
    flexDirection: {base: 'column !important', lg: 'row !important'},
    gap: {base: '0 !important', lg: '16px !important'},
    justifyContent: {base: 'flex-start !important', lg: 'space-between !important'},
    marginBottom: '28px',
    marginLeft: '0 !important',
    marginRight: '0 !important',
    paddingLeft: {base: '0 !important', lg: 'unset'},
    paddingRight: {base: '0 !important', lg: 'unset'},
    textAlign: {base: 'left !important', lg: 'unset'},
    width: '100%',
    // Mobile: title above full-width search (Figma / filter stack).
    '& > h3': {
      paddingTop: {base: '0 !important', lg: 'unset'},
      paddingBottom: {base: '0 !important', lg: 'unset'},
      width: {base: '100% !important', lg: 'auto'},
    },
    '& > div': {
      alignSelf: {base: 'stretch !important', lg: 'unset'},
      justifyContent: {base: 'flex-start !important', lg: 'flex-end !important'},
      width: {base: '100% !important', lg: 'auto'},
      marginBottom: {base: '0', lg: '0'},
      marginTop: {lg: '0'},
    },
  },
  // Desktop: kill BV chip margins (trailing 16px after Locale). Keep gap via sibling.
  [bvReviewsFilterChip]: {
    marginLeft: {lg: '0 !important'},
    marginRight: {lg: '0 !important'},
  },
  // Right operand must stay relative — full-root + full-root nests the section id.
  [`${bvReviewsFilterChip} + ${bvReviewsFilterChipRel}`]: {
    marginLeft: {lg: '16px !important'},
  },
  [bvReviewsCountRow]: {
    borderBottom: '2px solid #DADADA',
    paddingBottom: {lg: '44px'},
  },
  [bvReviewsCountBar]: {
    marginLeft: {lg: '0 !important'},
    marginRight: {lg: '0 !important'},
  },
  [bvReviewsPaginationRow]: {
    marginTop: '44px !important',
  },
  [bvReviewsPaginationBar]: {
    marginLeft: '0 !important',
    marginRight: '0 !important',
  },
  [bvReviewsPaginationControls]: {
    marginLeft: '0 !important',
    marginRight: '0 !important',
  },
  [bvReviewsContentItem]: {
    boxSizing: 'border-box !important',
    gap: {base: '24px', lg: '80px !important'},
    margin: '0 !important',
    maxWidth: '100% !important',
    width: '100% !important',
  },
  [bvReviewsContentBody]: {
    boxSizing: 'border-box !important',
    maxWidth: 'unset !important',
    minWidth: 'unset !important',
    width: '100% !important',
  },
  [bvReviewsItemWrapper]: {
    margin: '0 !important',
    padding: '0 !important',
  },
  [bvReviewsItemStarsRow]: {
    alignItems: 'center !important',
    alignSelf: 'flex-start !important',
    display: 'flex !important',
    justifyContent: 'flex-start !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    width: 'auto !important',
  },
  // BV often wraps stars in a full-width flex/button shell with justify center.
  [`${bvReviewsItemStarsRow} button, ${bvReviewsItemStarsBlock} button:has(abbr[aria-hidden="true"])`]: {
    justifyContent: 'flex-start !important',
    margin: '0 !important',
    marginLeft: '0 !important',
    marginRight: '0 !important',
  },
  [bvReviewsItemContentPad]: {
    boxSizing: 'border-box !important',
    margin: '0 !important',
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsMobileToolbarOuter]: {
    boxSizing: 'border-box !important',
    margin: '0 !important',
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsInlineProfile]: {
    alignItems: 'flex-start !important',
    display: 'flex !important',
    flexDirection: 'column !important',
    gap: {base: '4px !important', lg: '8px !important'},
    marginRight: '0 !important',
    maxWidth: 'unset !important',
    minWidth: 'unset !important',
    paddingTop: '0 !important',
    width: {base: '100% !important', lg: 'auto !important'},
  },
  [bvReviewsProfileEmkap]: {
    margin: '0 !important',
  },
  [`${bvReviewsInlineProfile} > div`]: {
    alignItems: 'flex-start !important',
    display: 'flex !important',
    flexDirection: 'column !important',
    gap: {base: '4px !important', lg: '8px !important'},
    margin: '0 !important',
    padding: '0 !important',
    width: '100% !important',
  },
  [`${bvReviewsInlineProfile} > div > div:last-child`]: {
    alignItems: 'flex-start !important',
    display: 'flex !important',
    flexDirection: 'column !important',
    gap: {base: '4px !important', lg: '8px !important'},
    margin: '0 !important',
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsProfileStatsNest]: {
    margin: '0 !important',
  },
  [bvReviewsProfileEmptyAvatar]: {
    display: 'none !important',
  },
  [bvReviewsProfileEmptyLocation]: {
    display: 'none !important',
  },
  [bvReviewsProfileUsernameButton]: {
    alignItems: 'flex-start !important',
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    cursor: 'pointer !important',
    display: 'inline-flex !important',
    justifyContent: 'flex-start !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    width: 'auto !important',
    '&:hover, &:focus': {
      background: 'transparent !important',
      boxShadow: 'none !important',
    },
  },
  [bvReviewsProfileUsername]: {
    color: '#32312F !important',
    fontFamily: 'body !important',
    fontSize: {base: '14px !important', lg: '16px !important'},
    fontStyle: 'normal !important',
    fontVariantNumeric: 'lining-nums proportional-nums !important',
    fontWeight: {base: '600 !important', lg: '400 !important'},
    letterSpacing: '0 !important',
    lineHeight: '1.4 !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'none !important',
  },
  [bvReviewsProfileStatsLabel]: {
    ...typography.textStyles.bodyS,
    color: 'rgba(27, 26, 24, 0.70) !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'none !important',
  },
  [bvReviewsProfileStatsCount]: {
    ...typography.textStyles.bodyS,
    color: '#1B1A18 !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'none !important',
  },
  [bvReviewsItemTitleDateRow]: {
    alignItems: 'center !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    gap: '16px !important',
    justifyContent: 'space-between !important',
    width: '100% !important',
  },
  [bvReviewsItemTitle]: {
    color: '#000 !important',
    flex: '1 1 auto !important',
    fontFamily: 'body !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    minWidth: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    textTransform: 'none !important',
  },
  [bvReviewsItemDateWrap]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    display: 'inline-flex !important',
    flex: '0 0 auto !important',
    margin: '0 !important',
    padding: '0 !important',
    whiteSpace: 'nowrap !important',
    width: 'auto !important',
  },
  [bvReviewsItemMetaInner]: {
    alignItems: 'center !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexWrap: 'nowrap !important',
    gap: '8px !important',
    justifyContent: 'flex-end !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsItemMetaAuthorSlot]: {
    // Hide duplicate author chip beside title-row date; keep profile-column username.
    display: 'none !important',
  },
  [bvReviewsItemMetaBadgeSlot]: {
    // Hide duplicate Verified badge beside title-row date; keep profile-column badge.
    display: 'none !important',
  },
  [bvReviewsItemMetaDateSlot]: {
    alignItems: 'center !important',
    display: 'inline-flex !important',
    flex: '0 0 auto !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsItemDate]: {
    ...typography.textStyles.bodyS,
    alignSelf: 'center !important',
    color: 'rgba(27, 26, 24, 0.70) !important',
    display: 'inline-block !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'right !important',
    textTransform: 'none !important',
    whiteSpace: 'nowrap !important',
  },
  [bvReviewsItemBody]: {
    color: '#000 !important',
    fontFamily: 'body !important',
    fontSize: {base: '14px !important', lg: '16px !important'},
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.4 !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'none !important',
  },
  [bvReviewsResponseBox]: {
    border: '1px solid #E8E8E8 !important',
    borderRadius: '0 !important',
    boxSizing: 'border-box !important',
    margin: '24px 0 0 0 !important',
    overflow: 'hidden !important',
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsResponseHeader]: {
    alignItems: 'center !important',
    border: 'none !important',
    borderBottom: '1px solid #E8E8E8 !important',
    boxSizing: 'border-box !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    flexWrap: 'nowrap !important',
    gap: '8px !important',
    margin: '0 !important',
    padding: {base: '12px !important', lg: '16px !important'},
    width: '100% !important',
  },
  [bvReviewsResponseHeaderIcon]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    display: 'inline-flex !important',
    flex: '0 0 auto !important',
    flexBasis: 'auto !important',
    margin: '0 !important',
    maxWidth: 'none !important',
    padding: '0 !important',
    width: 'auto !important',
    '& svg': {
      display: 'block !important',
      flexShrink: '0 !important',
      height: '20px !important',
      width: '20px !important',
    },
  },
  [bvReviewsResponseHeaderMeta]: {
    alignItems: 'center !important',
    display: 'flex !important',
    flex: '1 1 auto !important',
    flexDirection: 'row !important',
    flexWrap: 'nowrap !important',
    gap: {base: '8px !important', lg: '16px !important'},
    justifyContent: 'space-between !important',
    margin: '0 !important',
    minWidth: '0 !important',
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsResponseLabelCell]: {
    flex: '1 1 auto !important',
    margin: '0 !important',
    minWidth: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsResponseLabel]: {
    color: '#1B1A18 !important',
    display: 'block !important',
    fontFamily: 'body !important',
    fontSize: {base: '14px !important', lg: '16px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.4 !important',
    margin: '0 !important',
    // Keep label + date on one row; label may wrap within its cell.
    maxWidth: '100% !important',
    overflowWrap: 'anywhere !important',
    padding: '0 !important',
    textTransform: 'none !important',
    whiteSpace: 'normal !important',
    width: 'auto !important',
  },
  [bvReviewsResponseDateCell]: {
    alignSelf: 'center !important',
    flex: '0 0 auto !important',
    flexShrink: '0 !important',
    margin: '0 0 0 auto !important',
    padding: '0 !important',
    textAlign: 'right !important',
    width: 'auto !important',
    '& > div': {
      margin: '0 !important',
      padding: '0 !important',
      width: 'auto !important',
    },
  },
  [bvReviewsResponseDate]: {
    color: 'rgba(27, 26, 24, 0.70) !important',
    display: 'inline-block !important',
    fontFamily: 'body !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'right !important',
    whiteSpace: 'nowrap !important',
  },
  [bvReviewsResponseContent]: {
    border: 'none !important',
    margin: '0 !important',
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsResponseContentInner]: {
    border: 'none !important',
    boxSizing: 'border-box !important',
    display: 'flex !important',
    flexDirection: 'column !important',
    gap: '16px !important',
    margin: '0 !important',
    padding: '16px !important',
    width: '100% !important',
  },
  [bvReviewsResponseContentTitle]: {
    color: '#000 !important',
    fontFamily: 'body !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    textTransform: 'none !important',
  },
  [bvReviewsResponseContentBody]: {
    color: '#000 !important',
    fontFamily: 'body !important',
    fontSize: {base: '14px !important', lg: '16px !important'},
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.4 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    textTransform: 'none !important',
  },
  [bvReviewsReviewRow]: {
    borderTop: '1px solid #E8E8E8 !important',
    paddingBottom: {base: '12px !important', lg: '32px !important'},
    paddingTop: {base: '12px !important', lg: '32px !important'},
  },
  // Mobile: previous review has reply → hide next review border-top (reply box already bordered).
  // Require a nested response box (`… > div:nth-child(2) > div`) — avoid nested `:has()`.
  [`${bvReviewsSection} section[id^="bv-review-"]:has([data-bv-v="contentItem"] > div:not([data-bv-v]) > div:nth-child(2) > div) + section[id^="bv-review-"]`]: {
    borderTop: {base: 'none !important', lg: '1px solid #E8E8E8 !important'},
  },
  [`${bvReviewsReviewRow}:last-of-type`]: {
    paddingBottom: {base: '0 !important', lg: '32px !important'},
    marginBottom: '0 !important',
  },
  [bvReviewsMobileToolbarRow]: {
    alignItems: 'stretch !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    gap: '8px !important',
    margin: {base: '0 0 16px 0 !important', lg: '0 !important'},
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsMobileToolbarCell]: {
    display: 'flex !important',
    flex: {base: '1 1 50% !important', lg: 'unset'},
    height: 'auto !important',
    margin: '0 !important',
    minHeight: '0 !important',
    minWidth: '0 !important',
    padding: '0 !important',
    width: {base: '100% !important', lg: 'auto !important'},
  },
  [bvReviewsMobileToolbarButton]: {
    alignItems: 'center !important',
    alignSelf: 'stretch !important',
    backgroundColor: 'white !important',
    border: '1px solid rgba(27, 26, 24, 0.9) !important',
    // Independent 4px corners (current live UI). Do not compose
    // `${cell}:first-child ${button}` — both selectors already include the
    // section root, so the joined selector nests the id in itself and never matches.
    borderRadius: '4px !important',
    boxSizing: 'border-box !important',
    color: '#1B1A18 !important',
    cursor: 'pointer !important',
    display: 'flex !important',
    flex: '1 1 auto !important',
    flexDirection: 'row !important',
    flexWrap: 'nowrap !important',
    gap: '8px !important',
    height: 'auto !important',
    justifyContent: 'center !important',
    margin: '0 !important',
    maxHeight: 'none !important',
    minHeight: {base: '44px !important', lg: '0 !important'},
    padding: {base: '10px 12px !important', lg: 'unset'},
    width: '100% !important',
  },
  [bvReviewsMobileToolbarButtonIcon]: {
    alignItems: 'center !important',
    display: 'flex !important',
    flexShrink: '0 !important',
    lineHeight: '0 !important',
    margin: '0 !important',
    padding: '0 !important',
    '& svg': {
      display: 'block !important',
      flexShrink: '0 !important',
      height: '17px !important',
      width: '17px !important',
    },
  },
  [bvReviewsMobileToolbarButtonBody]: {
    alignItems: {base: 'center !important', lg: 'unset'},
    display: 'flex !important',
    flexDirection: 'column !important',
    gap: '2px !important',
    justifyContent: 'center !important',
    margin: '0 !important',
    minWidth: '0 !important',
    padding: '0 !important',
    textAlign: 'center !important',
    width: '100% !important',
    '&:not(:has(> div))': {
      ...typography.textStyles.labelS,
      fontSize: '13px !important',
      fontWeight: '600 !important',
      lineHeight: '1.2 !important',
    },
  },
  [bvReviewsMobileToolbarSortLabel]: {
    ...typography.textStyles.labelS,
    color: '#1B1A18 !important',
    fontSize: '13px !important',
    fontWeight: '600 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    whiteSpace: 'nowrap !important',
  },
  [bvReviewsMobileToolbarSortValue]: {
    ...typography.textStyles.bodyS,
    color: '#1B1A18 !important',
    fontSize: '13px !important',
    fontWeight: '400 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    whiteSpace: 'nowrap !important',
  },
  [bvReviewsMobileToolbarSortButton]: {
    justifyContent: 'flex-start !important',
    padding: {base: '10px 16px !important', lg: 'unset'},
  },
  [bvReviewsMobileToolbarSortStack]: {
    display: 'flex !important',
    flex: '1 1 auto !important',
    margin: '0 !important',
    width: '100% !important',
  },
  [bvReviewsSortStack]: {
    alignItems: 'center !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    flexWrap: 'wrap !important',
    gap: '16px !important',
    // Desktop: Sort + Relevancy sit on the right of the count row.
    justifyContent: {base: 'flex-start !important', lg: 'flex-end !important'},
    lineHeight: 'normal !important',
    margin: '0 !important',
    padding: '0 !important',
    width: {base: '100% !important', lg: 'auto !important'},
  },
  [bvReviewsSortInner]: {
    alignItems: 'center !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    flexWrap: 'wrap !important',
    gap: '16px !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  // Strip BV's outer gray box so only `bvReviewsSortControl` shows the border.
  [bvReviewsSortDropdown]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    display: 'inline-flex !important',
    height: 'auto !important',
    margin: '0 !important',
    minHeight: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsSortDropdownMid]: {
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    display: 'inline-flex !important',
    height: 'auto !important',
    margin: '0 !important',
    minHeight: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsSortShell]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    display: 'inline-flex !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsSortControl]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    backgroundColor: 'white !important',
    border: '1px solid rgba(27, 26, 24, 0.9) !important',
    borderRadius: '4px !important',
    boxSizing: 'border-box !important',
    cursor: 'pointer !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    flexWrap: 'nowrap !important',
    gap: '0 !important',
    height: 'auto !important',
    justifyContent: 'center !important',
    margin: '0 !important',
    minHeight: '0 !important',
    padding: {base: '10px 16px !important', lg: '12px 24px !important'},
    width: 'auto !important',
    '& > div': {
      alignItems: 'center !important',
      alignSelf: 'center !important',
      display: 'flex !important',
      flexShrink: '0 !important',
      margin: '0 !important',
      padding: '0 !important',
    },
    '& > div:has(svg)': {
      alignItems: 'center !important',
      display: 'flex !important',
      flexShrink: '0 !important',
      lineHeight: '0 !important',
      margin: '0 0 0 8px !important',
      padding: '0 !important',
    },
    '& svg': {
      display: 'block !important',
      flexShrink: '0 !important',
      height: '12px !important',
      margin: '0 !important',
      width: '12px !important',
    },
  },
  [bvReviewsSortLabel]: {
    ...typography.textStyles.labelS,
    alignItems: 'center !important',
    alignSelf: 'center !important',
    color: '#1B1A18 !important',
    display: 'inline-flex !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'none !important',
    whiteSpace: 'nowrap',
    '&::after': {
      content: '": "',
    },
  },
  [bvReviewsSortCombobox]: {
    ...typography.textStyles.labelS,
    alignItems: 'center !important',
    alignSelf: 'center !important',
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    color: '#1B1A18 !important',
    cursor: 'pointer !important',
    display: 'inline-flex !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    marginTop: '0 !important',
    marginRight: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '5px !important',
    minHeight: '0 !important',
    outline: 'none !important',
    padding: '0 !important',
    textTransform: 'none !important',
    width: 'auto !important',
  },
  [`${bvReviewsSortCombobox} span, ${bvReviewsSortCombobox} div`]: {
    alignItems: 'center !important',
    color: 'inherit !important',
    display: 'inline-flex !important',
    fontSize: 'inherit !important',
    fontWeight: 'inherit !important',
    lineHeight: 'inherit !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'inherit !important',
  },
  [bvReviewsRelevancyWrap]: {
    alignItems: 'center !important',
    alignSelf: {base: 'stretch !important', lg: 'center !important'},
    display: 'inline-flex !important',
    flexShrink: '0 !important',
    justifyContent: {base: 'flex-start !important', lg: 'flex-end !important'},
    margin: '0 !important',
    padding: '0 !important',
    textAlign: {base: 'left !important', lg: 'right !important'},
    width: {base: '100% !important', lg: 'auto !important'},
    '& > span': {
      alignItems: 'center !important',
      display: 'inline-flex !important',
      justifyContent: {base: 'flex-start !important', lg: 'flex-end !important'},
      margin: '0 !important',
      padding: '0 !important',
      width: 'auto !important',
    },
  },
  [bvRelevancyInfo]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    color: '#282829 !important',
    cursor: 'pointer !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    flexWrap: 'nowrap !important',
    fontFamily: 'body',
    fontSize: '12px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    gap: '4px !important',
    justifyContent: 'flex-start !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    textDecoration: 'none !important',
    textTransform: 'none !important',
    whiteSpace: 'nowrap !important',
    width: 'auto !important',
    '& svg': {
      display: 'block !important',
      flexShrink: '0 !important',
      height: '16px !important',
      maxHeight: '16px !important',
      maxWidth: '16px !important',
      width: '16px !important',
    },
    '&:hover, &:focus': {
      background: 'transparent !important',
      color: '#282829 !important',
      textDecoration: 'none !important',
    },
  },
  [bvRelevancyInfoLabel]: {
    alignItems: 'center !important',
    color: '#282829 !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexWrap: 'nowrap !important',
    fontSize: '12px !important',
    fontWeight: '400 !important',
    gap: '4px !important',
    lineHeight: '1.2 !important',
    margin: {base: '0 0 14px 0 !important', lg: '0 !important'},
    padding: '0 !important',
    textDecoration: 'none !important',
    whiteSpace: 'nowrap !important',
    '& > span': {
      display: 'inline !important',
      margin: '0 !important',
      padding: '0 !important',
      whiteSpace: 'nowrap !important',
    },
  },
  [bvRelevancySrOnly]: {
    border: '0 !important',
    clip: 'rect(0 0 0 0) !important',
    height: '1px !important',
    margin: '-1px !important',
    overflow: 'hidden !important',
    padding: '0 !important',
    position: 'absolute !important',
    whiteSpace: 'nowrap !important',
    width: '1px !important',
  },
  [bvRelevancyPopup]: {
    whiteSpace: 'normal !important',
    width: '295px !important',
    left: {base: '0 !important', lg: 'unset'},
    '& > div > div:first-child': {
      left: {base: '45px !important', lg: 'unset'},
    },
  },

  [bvRelevancyPopupClose]: {
    float: 'right !important',
  },
  [`${bvReviewsSection} div:has(> button abbr[aria-hidden="true"])`]: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '8px',
  },
  [`${bvReviewsSection} [data-bv-v="ratingSummaryContainer"]`]: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '8px',
  },
  [`${bvReviewsSection} [data-bv-v="ratingSummaryStars"]`]: {
    display: 'inline-flex',
    flexShrink: 0,
    flexWrap: 'nowrap',
    width: 'auto',
  },
  [`${bvReviewsSection} button:has(abbr[aria-hidden="true"])`]: {
    display: 'inline-flex',
    flexShrink: 0,
    width: 'auto',
  },
  [`${bvReviewsSection} button:has(abbr[aria-hidden="true"]) > div`]: {
    alignItems: 'center',
    display: 'inline-flex',
    flexWrap: 'nowrap',
    width: 'auto',
  },
  [bvReviewsStarsAbbr]: {
    alignItems: 'center',
    borderBottom: 'none',
    display: 'inline-flex',
    flexDirection: 'row',
    flexShrink: 0,
    flexWrap: 'nowrap',
    gap: '2px',
    height: 'auto',
    lineHeight: 0,
    maxWidth: 'none',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    width: 'auto',
    '& svg': {
      display: 'block',
      flexShrink: 0,
      height: '16px',
      width: '16px',
    },
  },
  [bvVerifiedBadgeList]: {
    alignItems: 'flex-start !important',
    display: 'flex !important',
    flexDirection: 'column !important',
    listStyle: 'none !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
    '& > li': {
      alignItems: 'flex-start !important',
      display: 'flex !important',
      listStyle: 'none !important',
      margin: '0 !important',
      padding: '0 !important',
      width: 'auto !important',
    },
  },
  [bvVerifiedBadgeSlot]: {
    alignSelf: 'flex-start !important',
    display: 'block !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
    // Empty slot still participates in parent layout — collapse it.
    '&:empty': {
      display: 'none !important',
      margin: '0 !important',
      padding: '0 !important',
    },
  },
  [bvVerifiedBadge]: {
    alignItems: 'center !important',
    alignSelf: 'flex-start !important',
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    color: 'inherit !important',
    cursor: 'default !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    flexWrap: 'nowrap !important',
    fontWeight: 'normal !important',
    gap: '4px !important',
    height: 'auto !important',
    justifyContent: 'flex-start !important',
    lineHeight: 'normal !important',
    margin: '0 !important',
    minHeight: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    verticalAlign: 'middle !important',
    width: 'auto !important',
  },
  [bvVerifiedBadgeWrap]: {
    alignItems: 'center !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    gap: '4px !important',
    margin: '0 !important',
    padding: '0 !important',
  },
  [bvVerifiedBadgeText]: {
    color: {base: '#2D7831 !important', lg: '#4CAF6E !important'},
    fontFamily: 'body !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    order: '1 !important',
    padding: '0 !important',
    textTransform: 'none !important',
    whiteSpace: 'nowrap !important',
  },
  [bvVerifiedBadgeIcon]: {
    alignItems: 'center !important',
    display: 'inline-flex !important',
    flexShrink: '0 !important',
    lineHeight: '0 !important',
    margin: '0 !important',
    order: '2 !important',
    padding: '0 !important',
    // BV ships `position: relative; top: 5px` on the SVG — reset so text + icon sit middle.
    '& svg': {
      display: 'block !important',
      height: '14px !important',
      margin: '0 !important',
      position: 'static !important',
      top: '0 !important',
      width: '14px !important',
    },
  },
  [`${bvReviewsSection} ul:has(> li .prev)`]: {
    display: 'flex',
    gap: '8px',
    listStyle: 'none',
    margin: '0',
    padding: '0',
  },
  [`${bvReviewsSection} ul:has(> li .prev) > li`]: {
    display: 'flex',
    listStyle: 'none',
    margin: '0',
    padding: '0',
  },
  [bvReviewsPaginationPrev]: {
    ...bvReviewsPaginationButtonBase,
  },
  [bvReviewsPaginationNext]: {
    ...bvReviewsPaginationButtonBase,
  },
  [bvReviewsPaginationPrevDisabled]: {
    backgroundColor: '#E8E8E8',
    border: 'none',
    cursor: 'not-allowed',
    pointerEvents: 'none',
    '& svg': {
      opacity: 0.5,
    },
  },
  [bvReviewsPaginationNextDisabled]: {
    backgroundColor: '#E8E8E8',
    border: 'none',
    cursor: 'not-allowed',
    pointerEvents: 'none',
    '& svg': {
      opacity: 0.5,
    },
  },
};
